<template>
  <div class="config-params-tree">
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="config-params-tree__loading"
    >
      <b-spinner
        variant="primary"
        class="mr-1"
      />
    </div>

    <template v-else>
      <!-- Search field -->
      <input
        v-model="search"
        class="form-control config-params-tree__search"
        placeholder="Buscar..."
      >

      <!-- Tree nodes -->
      <div ref="nodesContainer" class="config-params-tree__nodes">
        <template v-for="node in filteredTree">
          <config-params-tree-node
            :key="node.optionId || node.id"
            :node="node"
            :level="0"
            :selected-id="selectedId"
            :is-super-admin="isSuperAdmin"
            :expanded-nodes="expandedNodes"
            :force-expand-all="isSearchActive"
            @select-item="onSelectItem"
            @toggle-expand="onToggleExpand"
            @context-action="onContextAction"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { BSpinner, BIcon } from 'bootstrap-vue'
import { filterTree } from '@/febos/global/empresas/servicios/configParams.service'

/**
 * Recursive tree node component rendered inline.
 * Handles indentation, guide lines, icons, and expand/collapse.
 */
const ConfigParamsTreeNode = {
  name: 'ConfigParamsTreeNode',
  components: { BIcon },
  props: {
    node: { type: Object, required: true },
    level: { type: Number, required: true },
    selectedId: { type: String, default: null },
    isSuperAdmin: { type: Boolean, default: false },
    expandedNodes: { type: Set, required: true },
    forceExpandAll: { type: Boolean, default: false },
  },
  computed: {
    isFolder() {
      return this.node.isFolder
        || (this.node.children && this.node.children.length > 0)
    },
    isExpanded() {
      if (this.forceExpandAll) return true
      const nodeId = this.node.optionId || this.node.id
      return this.expandedNodes.has(nodeId)
    },
    isActive() {
      const nodeId = this.node.optionId || this.node.id
      return this.selectedId === nodeId
    },
    nodeStyle() {
      return { paddingLeft: `${this.level * 20}px` }
    },
    nodeClasses() {
      return {
        'tree-node': true,
        'tree-node--active': this.isActive,
        'tree-node--folder': this.isFolder,
      }
    },
    iconName() {
      if (!this.isFolder) return 'file-earmark-text'
      return this.isExpanded ? 'folder2-open' : 'folder'
    },
    guideLineStyle() {
      return this.level > 0
        ? { borderLeft: '1px solid #e0e0e0' }
        : {}
    },
  },
  methods: {
    handleClick() {
      if (this.isFolder) {
        this.$emit('toggle-expand', this.node)
      }
      this.$emit('select-item', this.node)
    },
    handleContextMenu(event) {
      if (this.isSuperAdmin) {
        event.preventDefault()
        this.$emit('context-action', { event, node: this.node })
      }
    },
  },
  render(h) {
    const children = []

    // Node row
    children.push(
      h('div', {
        class: this.nodeClasses,
        style: { ...this.nodeStyle, ...this.guideLineStyle },
        on: {
          click: this.handleClick,
          contextmenu: this.handleContextMenu,
        },
      }, [
        h('b-icon', {
          props: { icon: this.iconName },
          class: 'tree-node__icon',
        }),
        h('span', { class: 'tree-node__label' }, this.node.name),
      ])
    )

    // Recursive children (only if folder and expanded)
    if (this.isFolder && this.isExpanded && this.node.children) {
      const childNodes = this.node.children.map((child) =>
        h('config-params-tree-node', {
          key: child.optionId || child.id,
          props: {
            node: child,
            level: this.level + 1,
            selectedId: this.selectedId,
            isSuperAdmin: this.isSuperAdmin,
            expandedNodes: this.expandedNodes,
            forceExpandAll: this.forceExpandAll,
          },
          on: {
            'select-item': (item) => this.$emit('select-item', item),
            'toggle-expand': (item) => this.$emit('toggle-expand', item),
            'context-action': (payload) => this.$emit('context-action', payload),
          },
        })
      )
      children.push(...childNodes)
    }

    return h('div', { class: 'tree-node-wrapper' }, children)
  },
}

export default {
  name: 'ConfigParamsTree',
  components: {
    BSpinner,
    BIcon,
    ConfigParamsTreeNode,
  },
  props: {
    treeData: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedId: {
      type: String,
      default: null,
    },
    isSuperAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: '',
      expandedNodes: new Set(),
    }
  },
  computed: {
    filteredTree() {
      if (!this.search.trim()) return this.treeData
      return filterTree(this.treeData, this.search)
    },
    isSearchActive() {
      return this.search.trim().length > 0
    },
  },
  watch: {
    search(newVal, oldVal) {
      if (oldVal && !newVal.trim() && this.selectedId) {
        this.expandAncestorsOfSelected()
        this.$nextTick(() => this.scrollToSelectedNode())
      }
    },
  },
  methods: {
    onSelectItem(node) {
      this.$emit('select-item', node)
    },
    onToggleExpand(node) {
      const nodeId = node.optionId || node.id
      const updated = new Set(this.expandedNodes)
      if (updated.has(nodeId)) {
        updated.delete(nodeId)
      } else {
        updated.add(nodeId)
      }
      this.expandedNodes = updated
    },
    onContextAction({ event, node }) {
      this.$emit('context-action', { event, node })
    },
    expandAncestorsOfSelected() {
      const ancestors = this.findAncestors(this.treeData, this.selectedId)
      if (ancestors.length) {
        const updated = new Set(this.expandedNodes)
        ancestors.forEach((id) => updated.add(id))
        this.expandedNodes = updated
      }
    },
    findAncestors(nodes, targetId, path = []) {
      for (const node of nodes) {
        const nodeId = node.optionId || node.id
        if (nodeId === targetId) return path
        if (node.children && node.children.length > 0) {
          const result = this.findAncestors(node.children, targetId, [...path, nodeId])
          if (result) return result
        }
      }
      return null
    },
    scrollToSelectedNode() {
      const container = this.$refs.nodesContainer
      if (!container) return
      const activeNode = container.querySelector('.tree-node--active')
      if (activeNode) {
        activeNode.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    },
  },
}
</script>

<style scoped>
.config-params-tree {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.config-params-tree__loading {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.config-params-tree__search {
  flex-shrink: 0;
  margin-bottom: 0.5rem;
}

.config-params-tree__nodes {
  flex: 1;
  overflow-y: auto;
}
</style>

<style>
.config-params-tree .tree-node {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease;
  user-select: none;
}

.config-params-tree .tree-node:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.config-params-tree .tree-node--active {
  background-color: rgba(115, 103, 240, 0.15);
  font-weight: 600;
  border-left: 3px solid #7367f0;
  padding-left: 5px;
}

.config-params-tree .tree-node__icon {
  margin-right: 6px;
  flex-shrink: 0;
}

.config-params-tree .tree-node__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
